import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { useQuery } from '@apollo/react-hooks';
import { gql } from '@apollo/client';

import { useFlags } from '@atlaskit/flag';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import { token } from '@atlaskit/tokens';

import { LAND_IN_SAMPLE_SPACE_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import {
	Attribution,
	withTransparentErrorBoundary,
	isUnauthorizedError,
} from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';

const i18n = defineMessages({
	flagTitle: {
		id: 'experiment-land-in-sample-space-xflow.database.flag.title',
		defaultMessage: 'We’ve landed you in a database',
		description: 'Title text informing the user they are viewing a database',
	},
	flagBody: {
		id: 'experiment-land-in-sample-space-xflow.database.flag.body',
		defaultMessage:
			'Get started easily by trying out a database template from the right side panel.',
		description: 'Body text displayed in a flag telling the user to pick a database template',
	},
});

interface Props {
	spaceKey: string;
}

const DatabaseFlagComponent: React.FC<Props> = ({ spaceKey }) => {
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const { setOnboardingState } = useSetOnboardingState();

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([...Object.values(LAND_IN_SAMPLE_SPACE_ONBOARDING_STATE_KEYS)]);

	const { hasDismissedDatabaseFlag } = deserializeState(onboardingStateData);

	if (isUnauthorizedError(onboardingStateError)) {
		markErrorAsHandled(onboardingStateError);
	}

	const { data, error } = useQuery(
		gql`
			query DatabaseFlagSpaceTypeQuery($spaceKey: String) {
				space(key: $spaceKey) {
					type
				}
			}
		`,
		{
			variables: {
				spaceKey,
			},
			skip: hasDismissedDatabaseFlag || onboardingStateLoading || onboardingStateError,
		},
	);

	if (isUnauthorizedError(error)) {
		markErrorAsHandled(error);
	}

	const spaceType = data?.space.type;

	useEffect(() => {
		if (spaceType === 'xflow_sample_space' && !hasDismissedDatabaseFlag) {
			showFlag({
				title: formatMessage(i18n.flagTitle),
				description: formatMessage(i18n.flagBody),
				icon: <CheckCircleIcon label="check" color={token('color.icon.accent.green')} />,
				onDismissed: () => {
					setOnboardingState({
						key: LAND_IN_SAMPLE_SPACE_ONBOARDING_STATE_KEYS.hasDismissedDatabaseFlag,
						value: 'true',
					});
				},
			});
		}
	}, [showFlag, formatMessage, spaceType, hasDismissedDatabaseFlag, setOnboardingState]);

	return <></>;
};

export const DatabaseFlag = withTransparentErrorBoundary({
	attribution: Attribution.FLYWHEEL_ACTIVATION,
})(DatabaseFlagComponent);
